// src/components/PrescriptionSettingsModal.tsx
//
// Ajustes del módulo de Recetas: el engranaje de la pantalla de Recetas.
//
// Junta las dos cosas que el profesional necesita administrar y que hasta
// ahora no tenían dónde vivir:
//
//   - La matrícula. Cargarla ya tenía pantalla propia
//     (/professional/habilitar-recetas), pero no había forma de quitarla:
//     antes se borraba sin querer desde el editor de perfil, y cuando se
//     arregló ese bug quedó sin ninguna salida.
//
//   - El logo que sale impreso en la receta. Las recetas en papel siempre
//     llevaron membrete; el PDF no tenía ninguno.
//
// Los datos se piden a /api/prescriptions/ajustes, que responde siempre por
// el usuario del token y no recibe ningún id.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { olvidarHabilitacionRecetas } from '../hooks/usePuedeEmitirRecetas';
import {
  X, IdCard, ImagePlus, Loader2, Trash2, CheckCircle2,
  AlertTriangle, ChevronRight, Info,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// El body de Express acepta 10 MB y base64 infla un tercio. Se corta bastante
// antes para poder avisar con un mensaje claro en vez de que falle el POST.
const MAX_LOGO_BYTES = 4 * 1024 * 1024;

interface Ajustes {
  profesionalId: number;
  matricula: string | null;
  jurisdiccion: string | null;
  validada: boolean;
  validadaEl: string | null;
  logoUrl: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Se avisa al cerrar si algo cambió, para que la pantalla se refresque. */
  onCambios?: () => void;
}

const PrescriptionSettingsModal: React.FC<Props> = ({ isOpen, onClose, onCambios }) => {
  const [ajustes, setAjustes] = useState<Ajustes | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subiendoLogo, setSubiendoLogo] = useState(false);
  const [borrandoLogo, setBorrandoLogo] = useState(false);
  const [confirmandoBaja, setConfirmandoBaja] = useState(false);
  const [borrandoMatricula, setBorrandoMatricula] = useState(false);
  const [huboCambios, setHuboCambios] = useState(false);
  const inputArchivo = useRef<HTMLInputElement>(null);

  const token = () => localStorage.getItem('token');

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/prescriptions/ajustes`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        setError(json.error || 'No pudimos cargar tus ajustes');
      } else {
        setAjustes(json);
      }
    } catch {
      setError('No pudimos conectarnos. Revisá tu conexión.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setConfirmandoBaja(false);
      setHuboCambios(false);
      cargar();
    }
  }, [isOpen, cargar]);

  const cerrar = () => {
    if (huboCambios) onCambios?.();
    onClose();
  };

  // ---------------------------------------------------------------- logo ---

  const elegirLogo = () => inputArchivo.current?.click();

  const subirLogo = async (archivo: File) => {
    if (!ajustes) return;

    if (!archivo.type.startsWith('image/')) {
      setError('El logo tiene que ser una imagen (PNG o JPG).');
      return;
    }
    if (archivo.size > MAX_LOGO_BYTES) {
      setError('La imagen pesa más de 4 MB. Probá con una más liviana.');
      return;
    }

    setError(null);
    setSubiendoLogo(true);
    try {
      // El backend recibe la imagen en base64 y la sube a Cloudinary; es el
      // mismo camino que ya usan la foto de perfil y la de portada.
      const base64: string = await new Promise((resolve, reject) => {
        const lector = new FileReader();
        lector.onload = () => resolve(String(lector.result));
        lector.onerror = () => reject(new Error('No se pudo leer el archivo'));
        lector.readAsDataURL(archivo);
      });

      const res = await fetch(`${API_URL}/api/professionals/${ajustes.profesionalId}/photos`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ photoType: 'receta_logo', imageData: base64, filename: archivo.name }),
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        setError(json.message || json.error || 'No pudimos subir el logo');
      } else {
        setAjustes((a) => (a ? { ...a, logoUrl: json.photoUrl } : a));
        setHuboCambios(true);
      }
    } catch {
      setError('No pudimos subir el logo. Probá de nuevo.');
    } finally {
      setSubiendoLogo(false);
      if (inputArchivo.current) inputArchivo.current.value = '';
    }
  };

  const quitarLogo = async () => {
    if (!ajustes) return;
    setError(null);
    setBorrandoLogo(true);
    try {
      const res = await fetch(
        `${API_URL}/api/professionals/${ajustes.profesionalId}/photos/receta_logo`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } }
      );
      const json = await res.json();
      if (!res.ok || json.success === false) {
        setError(json.message || json.error || 'No pudimos quitar el logo');
      } else {
        setAjustes((a) => (a ? { ...a, logoUrl: null } : a));
        setHuboCambios(true);
      }
    } catch {
      setError('No pudimos quitar el logo. Probá de nuevo.');
    } finally {
      setBorrandoLogo(false);
    }
  };

  // ----------------------------------------------------------- matrícula ---

  const quitarMatricula = async () => {
    setError(null);
    setBorrandoMatricula(true);
    try {
      const res = await fetch(`${API_URL}/api/prescriptions/habilitacion`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        setError(json.error || 'No pudimos quitar la matrícula');
      } else {
        setAjustes((a) =>
          a ? { ...a, matricula: null, jurisdiccion: null, validada: false, validadaEl: null } : a
        );
        setConfirmandoBaja(false);
        // Igual que al cargarla: el hook de los botones del calendario cachea
        // la habilitación, y si no se olvida siguen apareciendo sin matrícula.
        olvidarHabilitacionRecetas();
        setHuboCambios(true);
      }
    } catch {
      setError('No pudimos quitar la matrícula. Probá de nuevo.');
    } finally {
      setBorrandoMatricula(false);
    }
  };

  if (!isOpen) return null;

  const fecha = (iso: string | null) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch {
      return null;
    }
  };

  return createPortal(
    // text-left a propósito: la app hereda text-align:center de la regla .App
    // que trae la plantilla de Create React App.
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center text-left">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-[fadeIn_.15s_ease-out]"
        onClick={cerrar}
      />

      <div className="relative w-full sm:max-w-md bg-[#F2F2F7] rounded-t-[28px] sm:rounded-[28px] shadow-2xl overflow-hidden animate-[slideUp_.25s_cubic-bezier(.32,.72,0,1)] max-h-[92vh] flex flex-col">
        <div className="sm:hidden pt-3 pb-1 flex justify-center flex-shrink-0">
          <div className="w-9 h-1 rounded-full bg-gray-400/60" />
        </div>

        <div className="px-6 pt-4 pb-3 flex items-center justify-between flex-shrink-0">
          <h2 className="text-[20px] font-bold text-gray-900 tracking-tight">Ajustes de recetas</h2>
          <button
            onClick={cerrar}
            className="w-8 h-8 rounded-full bg-gray-300/50 hover:bg-gray-300 flex items-center justify-center transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4 text-gray-700" />
          </button>
        </div>

        <div className="px-5 pb-7 overflow-y-auto">
          {cargando && (
            <div className="py-14 flex flex-col items-center">
              <Loader2 className="w-7 h-7 text-[#0B7C87] animate-spin" />
              <p className="mt-3 text-[14px] text-gray-700">Cargando…</p>
            </div>
          )}

          {!cargando && error && (
            <div className="mb-4 flex items-start gap-2.5 bg-[#FF3B30]/10 border border-[#FF3B30]/25 rounded-2xl px-4 py-3">
              <AlertTriangle className="w-[18px] h-[18px] text-[#B3261E] flex-shrink-0 mt-0.5" />
              <p className="text-[13px] text-[#B3261E] leading-snug">{error}</p>
            </div>
          )}

          {!cargando && ajustes && (
            <>
              {/* ---------------------------- Matrícula ---------------------------- */}
              <p className="text-[13px] font-semibold text-gray-600 uppercase tracking-wide px-1 mb-2">
                Matrícula
              </p>

              <div className="bg-white rounded-2xl overflow-hidden mb-2">
                <div className="px-4 py-3.5">
                  {ajustes.matricula ? (
                    <>
                      <div className="flex items-center gap-2">
                        <IdCard className="w-[18px] h-[18px] text-[#0B7C87] flex-shrink-0" />
                        <span className="text-[17px] font-bold text-gray-900">
                          {ajustes.matricula}
                        </span>
                        {ajustes.jurisdiccion && (
                          <span className="text-[14px] text-gray-700">· {ajustes.jurisdiccion}</span>
                        )}
                      </div>
                      {ajustes.validada ? (
                        <p className="mt-1.5 text-[13px] text-[#1B7F3B] flex items-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                          Validada en REFEPS
                          {fecha(ajustes.validadaEl) ? ` el ${fecha(ajustes.validadaEl)}` : ''}
                        </p>
                      ) : (
                        <p className="mt-1.5 text-[13px] text-[#8A4B00]">
                          Cargada, pero todavía sin validar
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-[15px] text-gray-700 leading-snug">
                      Todavía no cargaste tu matrícula. Sin ella no podés emitir recetas.
                    </p>
                  )}
                </div>

                <button
                  onClick={() => {
                    window.location.href = '/professional/habilitar-recetas';
                  }}
                  className="w-full px-4 py-3.5 flex items-center justify-between border-t border-gray-200/80 active:bg-gray-50 transition-colors"
                >
                  <span className="text-[16px] text-[#0B7C87] font-semibold">
                    {ajustes.matricula ? 'Cambiar matrícula' : 'Cargar matrícula'}
                  </span>
                  <ChevronRight className="w-[18px] h-[18px] text-gray-400" />
                </button>

                {ajustes.matricula && !confirmandoBaja && (
                  <button
                    onClick={() => setConfirmandoBaja(true)}
                    className="w-full px-4 py-3.5 flex items-center gap-2 border-t border-gray-200/80 active:bg-gray-50 transition-colors"
                  >
                    <Trash2 className="w-[17px] h-[17px] text-[#B3261E]" />
                    <span className="text-[16px] text-[#B3261E] font-semibold">Quitar matrícula</span>
                  </button>
                )}

                {confirmandoBaja && (
                  <div className="px-4 py-3.5 border-t border-gray-200/80 bg-[#FF3B30]/[0.06]">
                    <p className="text-[13px] text-gray-800 leading-snug">
                      Vas a dejar de poder emitir recetas hasta que la cargues de nuevo.
                      Las recetas ya emitidas no se tocan: cada una guarda la matrícula con
                      la que se firmó.
                    </p>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={quitarMatricula}
                        disabled={borrandoMatricula}
                        className="flex-1 py-2.5 rounded-xl bg-[#B3261E] text-white text-[15px] font-semibold active:scale-[.98] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
                      >
                        {borrandoMatricula && <Loader2 className="w-4 h-4 animate-spin" />}
                        Sí, quitarla
                      </button>
                      <button
                        onClick={() => setConfirmandoBaja(false)}
                        disabled={borrandoMatricula}
                        className="flex-1 py-2.5 rounded-xl bg-white border border-gray-300 text-gray-800 text-[15px] font-semibold active:scale-[.98] transition-transform"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <p className="text-[12px] text-gray-600 px-1 mb-6 leading-snug">
                La matrícula se busca en el registro nacional por tu DNI. No se escribe a mano.
              </p>

              {/* ------------------------------- Logo ------------------------------ */}
              <p className="text-[13px] font-semibold text-gray-600 uppercase tracking-wide px-1 mb-2">
                Logo de la receta
              </p>

              <div className="bg-white rounded-2xl overflow-hidden mb-2">
                <div className="px-4 py-4">
                  {ajustes.logoUrl ? (
                    <div className="rounded-xl bg-[#F2F2F7] p-3 flex items-center justify-center">
                      <img
                        src={ajustes.logoUrl}
                        alt="Logo que sale en la receta"
                        className="max-h-24 w-auto object-contain"
                      />
                    </div>
                  ) : (
                    <div className="rounded-xl border-2 border-dashed border-gray-300 py-7 flex flex-col items-center">
                      <ImagePlus className="w-7 h-7 text-gray-400" />
                      <p className="mt-2 text-[13px] text-gray-600">Todavía no cargaste un logo</p>
                    </div>
                  )}
                </div>

                <input
                  ref={inputArchivo}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) subirLogo(f);
                  }}
                />

                <button
                  onClick={elegirLogo}
                  disabled={subiendoLogo || borrandoLogo}
                  className="w-full px-4 py-3.5 flex items-center justify-between border-t border-gray-200/80 active:bg-gray-50 transition-colors disabled:opacity-60"
                >
                  <span className="text-[16px] text-[#0B7C87] font-semibold">
                    {ajustes.logoUrl ? 'Cambiar logo' : 'Subir logo'}
                  </span>
                  {subiendoLogo ? (
                    <Loader2 className="w-[18px] h-[18px] text-[#0B7C87] animate-spin" />
                  ) : (
                    <ChevronRight className="w-[18px] h-[18px] text-gray-400" />
                  )}
                </button>

                {ajustes.logoUrl && (
                  <button
                    onClick={quitarLogo}
                    disabled={subiendoLogo || borrandoLogo}
                    className="w-full px-4 py-3.5 flex items-center gap-2 border-t border-gray-200/80 active:bg-gray-50 transition-colors disabled:opacity-60"
                  >
                    {borrandoLogo ? (
                      <Loader2 className="w-[17px] h-[17px] text-[#B3261E] animate-spin" />
                    ) : (
                      <Trash2 className="w-[17px] h-[17px] text-[#B3261E]" />
                    )}
                    <span className="text-[16px] text-[#B3261E] font-semibold">Quitar logo</span>
                  </button>
                )}
              </div>

              <div className="flex items-start gap-2 px-1 leading-snug mb-6">
                <Info className="w-[15px] h-[15px] text-gray-500 flex-shrink-0 mt-0.5" />
                <p className="text-[12px] text-gray-600">
                  Se imprime arriba de la receta, entre el número y el código de barras.
                  Andá a lo ancho y con fondo transparente si podés: PNG o JPG, hasta 4 MB.
                  Las recetas que ya emitiste conservan el logo que tenían.
                </p>
              </div>

              {/* ------------------------------ Seguridad ------------------------------ */}
              <p className="text-[13px] font-semibold text-gray-600 uppercase tracking-wide px-1 mb-2">
                Seguridad
              </p>

              <div className="bg-white rounded-2xl overflow-hidden mb-2">
                <a
                  href="/professional/profile-config?tab=security"
                  className="w-full px-4 py-3.5 flex items-center justify-between active:bg-gray-50 transition-colors"
                >
                  <div className="min-w-0 pr-3">
                    <p className="text-[16px] text-gray-900 font-semibold">
                      Verificación en dos pasos
                    </p>
                    <p className="text-[13px] text-gray-700 mt-0.5 leading-snug">
                      Un código además de tu contraseña al iniciar sesión.
                      Es opcional: las recetas funcionan igual sin esto.
                    </p>
                  </div>
                  <ChevronRight className="w-[18px] h-[18px] text-gray-400 flex-shrink-0" />
                </a>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(18px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
        @media (prefers-reduced-motion: reduce) {
          .animate-\\[fadeIn_\\.15s_ease-out\\], .animate-\\[slideUp_\\.25s_cubic-bezier\\(\\.32\\,\\.72\\,0\\,1\\)\\] { animation: none !important }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default PrescriptionSettingsModal;
